/**
 * Regole di selezione delle tematiche in modalità QUESTIONARIO.
 *
 * In Due Diligence la compilazione usa la logica a soglia di CompilaAssessmentComponent
 * (minLivelloRispostaAutomatica); queste funzioni valgono solo per il Questionario:
 * - `multirisposta` della tematica: una sola risposta (false/assente) o una o più (true);
 * - `multirisposta` della risposta: una sola sottorisposta (false/assente) o una o più (true);
 * - la risposta NC (punteggio nullo) è sempre esclusiva;
 * - una risposta selezionata si può sempre deselezionare.
 */

interface OpzioneSelezionabile {
  chiave: string;
  punteggio?: unknown;
}

interface TematicaSelezionabile {
  opzioni?: OpzioneSelezionabile[];
  risposte: string[] | null;
  multirisposta?: boolean;
}

interface SottorispostaSelezionabile {
  checked: boolean;
}

interface RispostaConSottorisposte<T extends SottorispostaSelezionabile> {
  multirisposta?: boolean;
  sottoOpzioni?: T[];
}

export interface EsitoSelezioneRisposta {
  /** Nuovo elenco delle chiavi delle risposte selezionate. */
  risposte: string[];
  /** Chiavi delle risposte che erano selezionate e non lo sono più. */
  rimosse: string[];
}

/**
 * Una risposta NC è una risposta senza punteggio (stessa regola della configurazione tematica).
 */
function isRispostaNC(opzione: OpzioneSelezionabile): boolean {
  return opzione.punteggio === null || opzione.punteggio === undefined;
}

/**
 * Calcola le risposte selezionate dopo il click su una risposta.
 */
export function selezionaRispostaQuestionario(
  tematica: TematicaSelezionabile,
  opzione: OpzioneSelezionabile,
  checked: boolean,
): EsitoSelezioneRisposta {
  const correnti = tematica.risposte ?? [];
  let risposte: string[];

  if (!checked) {
    risposte = correnti.filter((chiave) => chiave !== opzione.chiave);
  } else if (isRispostaNC(opzione) || !tematica.multirisposta) {
    risposte = [opzione.chiave];
  } else {
    const chiaviNC = new Set(
      (tematica.opzioni ?? []).filter(isRispostaNC).map((opz) => opz.chiave),
    );
    risposte = [
      ...correnti.filter((chiave) => !chiaviNC.has(chiave) && chiave !== opzione.chiave),
      opzione.chiave,
    ];
  }

  return {
    risposte,
    rimosse: correnti.filter((chiave) => !risposte.includes(chiave)),
  };
}

/**
 * Restituisce le sottorisposte sorelle da deselezionare quando si seleziona una sottorisposta
 * di una risposta a scelta singola.
 */
export function sottorisposteDaDeselezionare<T extends SottorispostaSelezionabile>(
  risposta: RispostaConSottorisposte<T>,
  sottorisposta: T,
  checked: boolean,
): T[] {
  if (!checked || risposta.multirisposta) {
    return [];
  }

  return (risposta.sottoOpzioni ?? []).filter((sotto) => sotto !== sottorisposta && sotto.checked);
}
